import { Fragment, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { removeJobIdFromQuestions } from 'helpers';
import { makeAutoObservable, observable, runInAction, toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconArrowRight } from '@components/icons/arrow-right';
import { IconTrash } from '@components/icons/trash';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useToaster } from '@components/toaster';
import { uuid } from '@utils/uuid';

import { api } from 'api';
import { Box } from 'components/Box';
import { FixedBottomActions } from 'components/FixedBottomActions';
import { Input } from 'components/Input';
import { JobSearch } from 'components/JobSearch';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { AnswerCard, iconForQuestionType, QuestionAnswer } from 'components/QuestionAnswer';
import { useAuthenticatedUser } from 'features/authentication';
import { Modal, useModal } from 'features/modals';
import { ApplicationForm } from 'models/applications';
import { useStore } from 'models/Provider';

import { ApplicationPageHeader } from './ApplicationPageHeader';

export const ApplicationMoveCopy = observer(({ application, move }) => {
  const { current, api, t } = useStore();
  const { showError, showSuccess } = useToaster();
  const { showModal } = useModal();
  const { state } = useLocation();
  const { companies } = useAuthenticatedUser();

  const goBack = () => {
    window.location.href = state.onCloseUrl;
  };
  function moveApplicationModal(newApplicationId) {
    showModal('MOVE_APPLICATION_MODAL', {
      _container: {
        autoWidth: false,
        closeOnOverlayClickOrEsc: false,
      },
      model: model,
      moved: model.move,
      newJobId: model.job.id,
      name: application.name,
      newJobTitle: model.job.title,
      modelCompanyId: model.company.id,
      oldJobTitle: application.job.title,
      currentApplicationId: application.id,
      newApplicationIdCopied: newApplicationId,
      currentApplicationJobId: application.job.id,
    });
  }

  const commit = async () => {
    model.setSaving(true);
    if (model.move) {
      const data = model.prepMoveData();
      const res = await api.applications.move(application.id, data);
      if (!res.error) {
        moveApplicationModal();
        showSuccess(t('application.move.success', application.name, model.job.title));
      } else {
        showError(t('application.copy.error', application.name, model.job.title));
      }
      return;
    }
    const data = model.prepCopyData();
    const res = await api.applications.create(data);
    if (!res.error) {
      moveApplicationModal(res.data.id);
      showSuccess(t('application.copy.success', application.name, model.job.title));
      return;
    } else {
      showError(t('application.copy.error', application.name, model.job.title));
    }
    model.setSaving(false);
  };

  useLayoutEffect(() => {
    model.init({ current, companies, api, application, move });
    application.loadCv();
    application.loadCoverLetter();
    return () => {
      model.reset();
    };
  }, [api, application, companies, current, move]);

  return (
    <div>
      {model.saving && <Loading fixed />}
      <ApplicationPageHeader application={application} />
      <PageContent alwaysScroll bottomGap={80}>
        {model.step === 0 && <Step0 application={application} goBack={goBack} commit={commit} />}
        {model.step === 1 && <Step1 application={application} commit={commit} />}
      </PageContent>
    </div>
  );
});

const Step1 = observer(({ application, commit }) => {
  const { t } = useStore();
  return (
    <>
      {model.anyFileLoading && <Loading />}
      <Step1Container>
        <Step1Section>
          <Box flex justifyContent="space-between">
            <Text font="heading2" text={t('application.movecopy.current.resp')} />
            <Box color="green100" h="24" w="24">
              <IconArrowRight />
            </Box>
          </Box>
          <Separator />
          {application.formQuestions.map((q) => {
            return <QuestionAnswer q={q} application={application} key={q.id || q.title} />;
          })}
        </Step1Section>
        <Step1Section>
          <Text font="heading2" color="green100" text={t('application.movecopy.new.resp')} />
          <Separator />
          {model.newForm.sections.map((section) => {
            return section.questions.map((question) => {
              return (
                <Fragment key={question.id || question.title}>
                  <Box flex alignItems="center" color="purple80" mt="16">
                    <Stack space={2} orientation="horizontal">
                      <Box w="24" h="24">
                        {iconForQuestionType(question.type)}
                      </Box>
                      <Text font="bold14" text={question.title} />
                    </Stack>
                  </Box>
                  <Box mt="8">
                    <RenderEditableAnswer question={question} />
                  </Box>
                </Fragment>
              );
            });
          })}
        </Step1Section>
      </Step1Container>
      <FixedBottomActions>
        <Button
          variant="secondary"
          isDisabled={model.saving || model.anyFileLoading}
          onClick={() => model.setStep(0)}
          text={t('application.movecopy.button.back')}
        />
        <Button
          isDisabled={model.saving || model.anyFileLoading}
          onClick={commit}
          text={
            model.move
              ? t('application.movecopy.button.move')
              : t('application.movecopy.button.copy')
          }
        />
      </FixedBottomActions>
    </>
  );
});

const RenderEditableAnswer = observer(({ question }) => {
  const { t } = useStore();
  switch (question.type) {
    case 'one_line':
      if (!question.value && model.editing !== question.id) {
        return (
          <PlaceholderBox
            disabled={model.anyFileLoading}
            onClick={() => {
              if (model.anyFileLoading) return;
              model.edit(question.id);
            }}
          >
            {t('application.movecopy.addresp')}
          </PlaceholderBox>
        );
      }
      return (
        <Input
          disabled={model.saving || model.anyFileLoading}
          onBlur={model.resetEditing}
          onChange={question.setValue}
          focusOnMount
          value={question.value || ''}
        />
      );
    case 'multi_line':
      if (!question.value && model.editing !== question.id) {
        return (
          <PlaceholderBox
            disabled={model.anyFileLoading}
            onClick={() => {
              if (model.anyFileLoading) return;
              model.edit(question.id);
            }}
          >
            {t('application.movecopy.addresp')}
          </PlaceholderBox>
        );
      }
      return (
        <TextArea
          isDisabled={model.saving || model.anyFileLoading}
          onBlur={model.resetEditing}
          onChange={question.setValue}
          autoFocus={!Boolean(question.value)}
          value={question.value || ''}
        />
      );
    case 'dropdown':
      return (
        <AnswerCard>
          <CheckboxContainer>
            {question.options.map((o) => (
              <Checkbox
                isDisabled={model.saving || model.anyFileLoading}
                onChange={() => question.toggleOption(o.value)}
                key={o.value}
                value={question.value === o.value}
                label={o.display}
              />
            ))}
          </CheckboxContainer>
        </AnswerCard>
      );
    case 'multidropdown':
      const value = question.value || [];
      return (
        <AnswerCard>
          <CheckboxContainer>
            {question.options.map((o) => (
              <Checkbox
                isDisabled={model.saving || model.anyFileLoading}
                onChange={() => question.toggleOption(o.value)}
                key={o.value}
                value={value.includes(o.value)}
                label={o.display}
              />
            ))}
          </CheckboxContainer>
        </AnswerCard>
      );
    case 'attachment':
      const files = model.files[question.id] ?? [];
      return (
        <AttachmentContainer>
          {files.map((obj) => (
            <FileField
              key={obj.id}
              file={obj.file}
              onChange={(e) => {
                if (model.anyFileLoading) return;
                model.setFile(e, question.id);
              }}
              onRemove={(e) => {
                if (model.anyFileLoading) return;
                e.stopPropagation();
                e.preventDefault();
                model.removeFile(question.id, obj.id);
              }}
            />
          ))}
          {(files.length === 0 || question.multiple) && (
            <FileField onChange={(e) => model.setFile(e, question.id)} />
          )}
        </AttachmentContainer>
      );
    default:
      return null;
  }
});

const FileField = observer(({ file, onChange, onRemove }) => {
  const { t } = useStore();
  const ref = useRef();

  return (
    <PlaceholderBox
      disabled={model.anyFileLoading}
      onClick={() => {
        ref.current.click();
      }}
    >
      <input
        disabled={model.saving || model.anyFileLoading}
        onChange={onChange}
        ref={ref}
        type="file"
        style={{ display: 'none' }}
      />
      {file ? (
        <FileBox disabled={model.anyFileLoading}>
          <Box
            flex
            justifyContent="space-between"
            alignItems="center"
            style={{ overflow: 'hidden', height: '2.5rem' }}
          >
            <Text font="regular14" ellipsis text={file.name} />
            <IconButton
              variant="ghost-purple"
              disabled={model.anyFileLoading}
              icon={<IconTrash />}
              onClick={onRemove}
            />
          </Box>
        </FileBox>
      ) : (
        <span>{t('application.movecopy.addfile')}</span>
      )}
    </PlaceholderBox>
  );
});

const Step0 = observer(({ application, goBack, commit }) => {
  const { t } = useStore();
  const { showModal } = useModal();

  return (
    <>
      <Step0Container>
        <Stack space={6}>
          <Text
            font="heading2"
            text={model.move ? t('application.move.title') : t('application.copy.title')}
          />

          {model.companies.length > 1 && (
            <Select
              label={t('application.movecopy.label.company')}
              isClearable={false}
              isSearchable={false}
              isDisabled={model.saving}
              onChange={model.setCompany}
              defaultValue={model.company ? model.company.id : null}
              options={model.displayCompanies}
            />
          )}
          {model.company && (
            <JobSearch
              label={t('application.movecopy.label.job')}
              key={model.company.id}
              onSelect={model.setJob}
              job={model.job}
              company={model.company}
              currentJob={application.job}
            />
          )}

          {model.job && (
            <Select
              isClearable={false}
              isSearchable={false}
              label={t('application.movecopy.label.stage')}
              disabled={model.saving}
              defaultValue={model.jobStageId}
              onChange={model.setStage}
              key={model.jobStageId}
              options={model.jobStages}
              placeholder={t('application.movecopy.selectstage')}
            />
          )}

          <Stack space={2}>
            <FormLabel text={t('application.movecopy.label.include')} />
            <Stack space={2}>
              {application.coverLetter && (
                <Checkbox
                  isDisabled={model.saving}
                  onChange={() => model.toggleInclude('coverLetter')}
                  label={t('application.movecopy.label.coverletter')}
                  value={model.include.coverLetter}
                />
              )}
              <Checkbox
                isDisabled={model.saving}
                onChange={() => model.toggleInclude('notes')}
                label={t('application.movecopy.label.notes')}
                value={model.include.notes}
              />
              <Checkbox
                isDisabled={model.saving}
                onChange={() => model.toggleInclude('ratings')}
                label={t('application.movecopy.label.ratings')}
                value={model.include.ratings}
              />
              <Checkbox
                isDisabled={model.saving}
                onChange={() => model.toggleInclude('files')}
                label={t('application.movecopy.label.files')}
                value={model.include.files}
              />
              <Checkbox
                isDisabled={model.saving}
                onChange={() => model.toggleInclude('responses')}
                label={t('application.movecopy.label.responses')}
                value={model.include.responses}
              />
            </Stack>

            {model.include.responses && <Text text={t('application.movecopy.responses.message')} />}
          </Stack>
          <Separator />
          {!model.allIncludesChecked && (
            <Text color="red100" text={t('application.movecopy.warning.message')} />
          )}
        </Stack>
      </Step0Container>
      <FixedBottomActions>
        <Button
          onClick={goBack}
          variant="secondary"
          text={t('application.movecopy.button.cancel')}
        />
        <Button
          disabled={!model.job || !model.jobStageId}
          onClick={() => {
            if (model.job && model.jobStageId) {
              if (!model.allIncludesChecked) {
                showModal('CONFIRM_MOVE_NO_QUESTIONS', {
                  onContinue: model.include.responses ? model.tryStep1 : commit,
                  submitText: model.include.responses
                    ? 'application.confirmcopy.proc'
                    : model.move
                      ? 'application.movecopy.button.move'
                      : 'application.movecopy.button.copy',
                });
              } else {
                model.tryStep1();
              }
            } else {
              model.tryStep1();
            }
          }}
          text={t('application.movecopy.button.cont')}
        />
      </FixedBottomActions>
    </>
  );
});

export const ConfirmNoQuestions = ({ closeModal, onContinue, submitText }) => {
  const { t } = useStore();
  const submit = async () => {
    closeModal();
    await onContinue();
  };
  return (
    <Modal>
      <Modal.Header text={t('application.confirmcopy.title')} />
      <Stack space={4}>
        <Text
          text={t(
            'application.confirmcopy.main',
            Object.keys(model.include)
              .filter((key) => !model.include[key])
              .map((key) => t(`application.movecopy.label.${key.toLowerCase()}`))
              .join(', '),
          )}
        />
        <Text font="bold16" color="red100" text={t('application.confirmcopy.warn')} />

        <Text text={t('application.confirmcopy.cta')} />
      </Stack>
      <Modal.Actions>
        <Button variant="secondary" onClick={closeModal} text={t('application.confirmcopy.back')} />
        <Button onClick={submit} text={t(submitText)} />
      </Modal.Actions>
    </Modal>
  );
};

const trimData = (application) => {
  return {
    email: application.email,
    isManuallyAdded: application.isManuallyAdded,
    language: application.language,
    name: application.name,
    phone: application.phone,
    referredBy: application.referredBy,
    referredSite: application.referredSite,
    assignedTo: application.assignedTo,
    ssn: application.ssn,
    ssnStatus: application.ssnStatus,
  };
};

class ApplicationMoveModel {
  step = 0;
  api = null;
  loadingJobs = false;
  loadingJobDetail = false;
  jobs = [];
  companies = [];
  job = null;
  company = null;
  jobStageId = '';
  saving = false;
  move = false;
  include = {
    coverLetter: true,
    notes: true,
    ratings: true,
    files: true,
    responses: true,
  };
  application = null;
  newForm = null;
  errors = {};
  editing = null;
  values = {};
  files = {};

  constructor() {
    makeAutoObservable(this);
  }

  get anyFileLoading() {
    const keys = Object.keys(this.files);
    for (let i = 0; i < keys.length; i++) {
      const files = this.files[keys[i]];
      for (let j = 0; j < files.length; j++) {
        if (files[j].loading) {
          return true;
        }
      }
    }
    return false;
  }

  get allIncludesChecked() {
    return Object.keys(this.include).every((key) => this.include[key]);
  }

  get displayCompanies() {
    return this.companies.map((company) => ({
      value: company.id,
      label: company.displayName,
    }));
  }

  prepMoveData = () => {
    const data = new FormData();

    data.append('jobStageId', this.jobStageId);
    data.append('job', this.job.id);

    Object.keys(this.files).forEach((key) => {
      const files = this.files[key];
      if (Array.isArray(files)) {
        files.forEach((f, index) => {
          data.append(`${index}__${key}`, f.file);
        });
      } else {
        data.append(key, this.files[key].file);
      }
    });

    data.append('coverLetter', this.include.coverLetter);
    data.append('notes', this.include.notes);
    data.append('ratings', this.include.ratings);
    data.append('attachments', this.include.files);

    if (this.include.responses) {
      data.append('form', JSON.stringify(toJS(this.newForm)));
    } else {
      for (let i = 0; i < this.job.form.sections[0].questions.length; i++) {
        this.job.form.sections[0].questions[i] = this.stripErrorAndHidden(
          this.job.form.sections[0].questions[i],
        );
      }
      data.append('form', JSON.stringify({ sections: this.job.form.sections }));
    }

    return data;
  };

  prepCopyData = () => {
    const data = new FormData();
    const trimmed = trimData(getSnapshot(this.application));
    Object.keys(trimmed).forEach((key) => {
      if (trimmed[key] !== undefined && trimmed[key] !== null) {
        data.append(key, trimmed[key]);
      }
    });

    data.append('jobStageId', this.jobStageId);

    Object.keys(this.files).forEach((key) => {
      this.files[key].forEach((obj) => {
        data.append(`${obj.id}__${key}`, obj.file);
      });
    });

    data.append(
      'copy_from',
      JSON.stringify({
        id: this.application.id,
        cover_letter: this.include.coverLetter,
        notes: this.include.notes,
        ratings: this.include.ratings,
        attachments: this.include.files,
      }),
    );

    if (this.include.responses) {
      data.append('form', JSON.stringify(toJS(this.newForm)));
    } else {
      for (let i = 0; i < this.job.form.sections[0].questions.length; i++) {
        this.job.form.sections[0].questions[i] = this.stripErrorAndHidden(
          this.job.form.sections[0].questions[i],
        );
      }
      data.append('form', JSON.stringify({ sections: this.job.form.sections }));
    }

    data.append('job', this.job.id);
    return data;
  };

  stripErrorAndHidden = (question) => {
    if (question.options) {
      question.options = question.options.map((option) => {
        const { error, ...rest } = option;
        return rest;
      });
    }
    const { error, hidden, ...rest } = question;
    return rest;
  };

  setSaving = (val) => {
    this.saving = val;
  };

  setFile = (e, questionId) => {
    if (e.target.files.length > 0) {
      if (this.files[questionId] === undefined) this.files[questionId] = [];

      this.files[questionId].push({
        id: uuid(),
        file: e.target.files[0],
      });
    }
  };

  removeFile = (questionId, fileId) => {
    this.files[questionId] = this.files[questionId].filter((obj) => obj.id !== fileId);
  };

  toggleInclude = (included) => {
    this.include[included] = !this.include[included];
  };

  init = async ({ companies, current, api, application, move }) => {
    this.move = move;
    this.api = api;
    this.companies = companies;
    this.company = current;
    this.application = application;
  };

  get jobStages() {
    if (!this.job) return [];
    return this.job.jobStages.map((stage) => ({ value: stage.id, label: stage.name }));
  }

  setStep = (step) => {
    this.step = step;
  };

  edit = (id) => {
    this.editing = id;
  };

  resetEditing = () => {
    this.editing = null;
  };

  setCompany = async (id) => {
    this.job = null;
    this.company = this.companies.find((company) => company.id === Number(id));
  };

  setStage = (id) => {
    this.jobStageId = id;
  };

  tryMigrate = () => {
    if (!this.application.form) return;
    const freeTextTypes = ['one_line', 'multi_line'];

    this.application.form.sections.forEach((section) => {
      const questions = removeJobIdFromQuestions(section.questions);
      questions.forEach((question) => {
        if (!question.value) return;
        this.newForm.sections.forEach((ns) => {
          ns.questions.forEach((nq) => {
            const matchesId = question.id === nq.id;
            const matchesTitle = question.title.toLowerCase() === nq.title.toLowerCase();
            // Doesnt matter if we migrate short -> long text or vice versa.
            const matchesFreeText =
              freeTextTypes.includes(question.type) && freeTextTypes.includes(nq.type);
            const matchesType = question.type === nq.type || matchesFreeText;
            if (!(matchesId || (matchesTitle && matchesType))) return;

            if (nq.type === 'attachment') {
              const matchingAttachments = this.application.attachments.filter((aa) => {
                return Array.isArray(question.value)
                  ? question.value.includes(aa.id)
                  : question.value === aa.id;
              });
              if (matchingAttachments.length > 0) {
                matchingAttachments.forEach((att) => {
                  const obj = observable({
                    id: uuid(),
                    file: null,
                    loading: true,
                  });
                  if (this.files[nq.id] === undefined) this.files[nq.id] = [];
                  this.files[nq.id].push(obj);
                  att.loadAttachment().then(() => {
                    if (!att.blob) return;
                    obj.loading = false;
                    obj.file = new File([att.blob], att.fileName, { type: att.blob.type });
                  });
                });
              }
              return;
            }

            if (Array.isArray(question.value)) {
              nq.setValue([...question.value]);
            } else {
              nq.setValue(question.value);
            }

            if (question.type === 'dropdown' || question.type === 'multidropdown') {
              if (!question.options) return;
              const allQ = question.options.every((o) =>
                nq.options.find((it) => it.value === o.value),
              );
              if (question.options.length === nq.options.length && allQ) {
                nq.setValue(question.value);
              }
            }
          });
        });
      });
    });
  };

  setJob = async (jobId) => {
    this.jobStageId = null;
    this.errors.job = undefined;
    this.loadingJobDetail = true;
    const res = await api.jobs.loadJob(jobId, this.company.id);
    if (!res.error) {
      const job = res.data[0];
      runInAction(() => {
        this.job = job;
      });
      const lang = this.application.language;
      const langObj = job.languages.find((obj) => obj.language === lang) || job.languages[0];

      const formWithoutJobIds = {
        sections: langObj.form.sections.map((section) => ({
          ...section,
          questions: removeJobIdFromQuestions([...section.questions]),
        })),
      };
      runInAction(() => {
        this.newForm = ApplicationForm.create(formWithoutJobIds);
      });
      this.tryMigrate();
    }
    runInAction(() => {
      this.loadingJobDetail = false;
    });
  };

  tryStep1 = () => {
    if (this.job === null) {
      this.errors.job = 'errors.required';
      return;
    }

    this.setStep(1);
  };

  reset = () => {
    this.saving = false;
    this.move = false;
    this.application = null;
    this.errors = {};
    this.values = {};
    this.files = {};
    this.editing = null;
    this.loadingJobs = false;
    this.loadingJobDetail = false;
    this.jobs = [];
    this.companies = [];
    this.job = null;
    this.newForm = null;
    this.company = null;
    this.jobStageId = '';
    this.step = 0;
    this.include.coverLetter = true;
    this.include.notes = true;
    this.include.ratings = true;
    this.include.files = true;
    this.include.responses = true;
  };
}

const model = new ApplicationMoveModel();
window.mdl = model;

const CheckboxContainer = styled.div((p) => ({
  '> div:not(:first-child)': {
    marginTop: p.theme.spacing.m8,
  },
}));

const Step1Section = styled.div(() => ({
  width: '100%',
  maxWidth: '23.75rem',
}));

const Step1Container = styled.div((p) => ({
  margin: 'auto',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  '> div:last-child': {
    marginLeft: p.theme.spacing.m24,
  },
}));

const Step0Container = styled.div(() => ({
  width: '100%',
  maxWidth: '23.75rem',
  margin: 'auto',
}));

const PlaceholderBox = styled.div((p) => ({
  height: '2.5rem',
  width: '100%',
  color: p.theme.colors.purple50,
  ...p.theme.text.regular14,
  border: '1px dashed ' + p.theme.colors.purpleTrans20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '0.25rem',
  ...(!p.disabled && { cursor: 'pointer' }),
}));

const FileBox = styled.div((p) => ({
  whiteSpace: 'no-wrap',
  backgroundColor: p.theme.colors.purpleTrans5,
  paddingLeft: '0.75rem',
  ...(!p.disabled && {
    ':hover': {
      cursor: 'pointer',
    },
  }),
  width: '100%',
  overflow: 'hidden',
  height: '2.5rem',
  textOverflow: 'ellipsis',
}));

const AttachmentContainer = styled.div({
  '> div:not(:first-child)': {
    marginTop: 4,
  },
});
